import { useCallback, useMemo } from "react";

import { UseFieldArrayReturn, UseFormReturn } from "react-hook-form";
import { v4 } from "uuid";

import { useUserInputContext } from "../../../../../contexts/UserInputContext";

import { TicketingPaymentInput } from "@/types/ticketing";
import { QuestionOutput, RateOutput } from "@/types/trpc";

function generateDefaultQuestionAnswer(questionId: string) {
  return {
    id: v4(),
    questionId,
    choiceIds: [],
    answer: "",
    answers: [""],
  };
}

export type UseTicketingV2CardStateOptions = {
  form: UseFormReturn<TicketingPaymentInput>;
  participants: UseFieldArrayReturn<TicketingPaymentInput, "customAnswersOfParticipants">;
  rates: RateOutput[];
  allRates: RateOutput[];
  questions: QuestionOutput[];
  animations?: {
    rotate?: () => void;
    reverseRotate?: () => void;
    triggerHearts?: () => void;
  };
  callbacks?: {
    onItemAmountChanged: ({ from, to, rate }: { from: number; to: number; rate: RateOutput }) => void;
  };
};

export function useTicketingV2CardState({
  participants,
  form,
  rates,
  allRates,
  questions,
  animations,
  callbacks,
}: UseTicketingV2CardStateOptions) {
  const { setValue } = form;
  const { addUserInput } = useUserInputContext();

  const {
    fields: participantsFields,
    insert: insertCustomAnswersOfParticipants,
    remove: removeCustomAnswersOfParticipants,
  } = participants;

  const rateIdToParticipantCount = useMemo(() => {
    return rates.reduce((acc: Record<string, number>, r) => {
      acc[r.id] = participantsFields.filter((p) => p.rateId === r.id).length;
      return acc;
    }, {});
  }, [participantsFields, rates]);

  const generateOnIncrementAmount = useCallback(
    ({ rateId, title }: { rateId: string; title: string }) => {
      return () => {
        const currentRateIndex = allRates.findIndex((x) => x.id === rateId);
        const currentRate = allRates[currentRateIndex];

        if (!currentRate) {
          // The rate no longer exists
          return;
        }

        const purchasedNumber = form.getValues(`ticketsPurchased.${currentRateIndex}.purchasedNumber`) || 0;
        const minimumToBuy = currentRate.minimumToBuy || 1;
        const attendeesByTicket = currentRate.attendeesByTicket || 1;
        const numberOfTicketsToAdd = purchasedNumber === 0 ? minimumToBuy : 1;
        const numberOfAttendeesToAdd = attendeesByTicket * numberOfTicketsToAdd;

        animations?.rotate?.();
        animations?.triggerHearts?.();

        const questionsForRate = questions.filter(
          (q) => q.hasRate && q.rateQuestions?.some((rq) => rq.rateId === rateId)
        );

        const newCustomAnswersOfParticipants = Array.from({ length: numberOfAttendeesToAdd }, () => ({
          rateId,
          answers: questionsForRate.map((question) => generateDefaultQuestionAnswer(question.id)),
        }));

        const newPurchasedNumber = purchasedNumber + numberOfTicketsToAdd;
        setValue(`ticketsPurchased.${currentRateIndex}.purchasedNumber`, newPurchasedNumber);

        addUserInput({
          [title]: newPurchasedNumber.toString(),
        });

        // Find the last index of the current rate
        const lastIndexOfRate = participantsFields
          .map((field, idx) => ({ field, idx }))
          .reverse()
          .find(({ field }) => field.rateId === rateId)?.idx;

        let insertionIndex: number;

        if (lastIndexOfRate !== undefined) {
          // Insert after the last existing participant of the current rate
          insertionIndex = lastIndexOfRate + 1;
        } else {
          // If no participants exist for this rate, calculate insertion index based on rate order
          insertionIndex = rates.slice(0, currentRateIndex).reduce((acc, r) => {
            return acc + (rateIdToParticipantCount[r.id] || 0);
          }, 0);
        }

        // Insert new participants sequentially
        newCustomAnswersOfParticipants.forEach((customAnswers, idx) => {
          insertCustomAnswersOfParticipants(insertionIndex + idx, customAnswers, { shouldFocus: false });
        });

        callbacks?.onItemAmountChanged?.({
          from: purchasedNumber,
          to: newPurchasedNumber,
          rate: currentRate,
        });
      };
    },
    [
      allRates,
      form,
      animations,
      questions,
      setValue,
      addUserInput,
      participantsFields,
      callbacks,
      rates,
      rateIdToParticipantCount,
      insertCustomAnswersOfParticipants,
    ]
  );

  const generateOnDecrementAmount = useCallback(
    ({ rateId, title }: { rateId: string; title: string }) => {
      return () => {
        const currentRateIndex = allRates.findIndex((x) => x.id === rateId);
        const currentRate = allRates[currentRateIndex];

        if (!currentRate) {
          // The rate no longer exists
          return;
        }

        animations?.reverseRotate?.();

        const purchasedNumber = form.getValues(`ticketsPurchased.${currentRateIndex}.purchasedNumber`) || 0;
        const minimumToBuy = currentRate.minimumToBuy || 1;
        const attendeesByTicket = currentRate.attendeesByTicket || 1;

        // Determine the new purchased number: allow going directly to 0 or decrement if above minimumToBuy
        const newPurchasedNumber = purchasedNumber > minimumToBuy ? purchasedNumber - 1 : 0;

        setValue(`ticketsPurchased.${currentRateIndex}.purchasedNumber`, newPurchasedNumber);
        addUserInput({
          [title]: newPurchasedNumber.toString(),
        });

        // Gather indices of participants associated with the current rate
        const participantsForRateIndices = participantsFields
          .map((field, idx) => ({ field, idx }))
          .filter(({ field }) => field.rateId === currentRate.id)
          .map(({ idx }) => idx);

        if (participantsForRateIndices.length === 0) {
          return;
        }

        // Calculate the total number of participants to remove
        const numberOfParticipantsToRemove =
          newPurchasedNumber === 0 ? participantsForRateIndices.length : attendeesByTicket;

        // Get the indices to remove based on the calculated number
        const indicesToRemove = participantsForRateIndices.slice(-numberOfParticipantsToRemove);

        // Sort indices in descending order to prevent index shifting during removal
        indicesToRemove.sort((a, b) => b - a);

        // Remove participants sequentially
        indicesToRemove.forEach((index) => {
          removeCustomAnswersOfParticipants(index);
        });

        callbacks?.onItemAmountChanged?.({
          from: purchasedNumber,
          to: newPurchasedNumber,
          rate: currentRate,
        });
      };
    },
    [
      addUserInput,
      allRates,
      animations,
      callbacks,
      form,
      participantsFields,
      removeCustomAnswersOfParticipants,
      setValue,
    ]
  );

  return {
    allParticipants: participantsFields,
    generateOnIncrementAmount,
    generateOnDecrementAmount,
  };
}
